import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, ImageOverlay, ZoomControl } from 'react-leaflet';
import { TextBox } from 'devextreme-react/text-box';
import { Popup } from 'devextreme-react/popup';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PublicSearch.css';
import { cemeteryApi } from '../services/api';
import type { CemeteryData } from '../types';

// Fix for default marker icons in React Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Cemetery bounds for Pokopališče Zagorje ob Savi
const CEMETERY_BOUNDS = {
  name: "Pokopališče Zagorje ob Savi",
  southwest: [46.133992, 14.997715] as [number, number],
  northeast: [46.135867, 14.999685] as [number, number],
  center: [46.1349295, 14.9987] as [number, number],
  zoom: 19
};

interface DeceasedPerson {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  maidenName?: string;
  fullName: string;
  birthDate?: string;
  deathDate?: string;
  displayBirthDate?: string;
  displayDeathDate?: string;
  age?: number;
  graveType?: string;
  graveSiteId: number;
  graveCode?: string;
  latitude?: number;
  longitude?: number;
  photoPath?: string;
}

// Component to fly to a location when selected
const FlyToLocation: React.FC<{ position: [number, number] }> = React.memo(({ position }) => {
  const map = useMap();
  const lastPositionRef = useRef<string>('');
  
  useEffect(() => {
    if (position && position[0] && position[1]) {
      const positionKey = `${position[0]},${position[1]}`;
      // Only fly if position actually changed
      if (positionKey !== lastPositionRef.current) {
        lastPositionRef.current = positionKey;
        map.flyTo(position, 20, {
          duration: 1.5
        });
      }
    }
  }, [position, map]);
  
  return null;
});

const PublicSearch: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [selectedDeceased, setSelectedDeceased] = useState<DeceasedPerson | null>(null);
  const [allDeceased, setAllDeceased] = useState<DeceasedPerson[]>([]);
  const [cemeteryData, setCemeteryData] = useState<CemeteryData | null>(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const mapRef = useRef<any>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  // Helper function to extract year from date string or return null
  const extractYear = (dateString?: string): string | null => {
    if (!dateString) return null;
    
    // Try to extract 4-digit year from the string
    const yearMatch = dateString.match(/\d{4}/);
    return yearMatch ? yearMatch[0] : null;
  };

  // Helper function to format display name with years
  const formatDisplayName = (deceased: DeceasedPerson): string => {
    const birthYear = extractYear(deceased.birthDate) || extractYear(deceased.displayBirthDate) || '?';
    const deathYear = extractYear(deceased.deathDate) || extractYear(deceased.displayDeathDate) || '?';
    
    return `${deceased.fullName} (${birthYear}-${deathYear})`;
  };

  // Load all deceased persons on mount
  useEffect(() => {
    loadAllDeceased();
    loadCemeteryData();
  }, []);

  // Click outside handler to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadCemeteryData = async () => {
    try {
      const data = await cemeteryApi.getPublicCemeteryData();
      if (data) {
        setCemeteryData(data);
      }
    } catch (error) {
      console.error('Error loading cemetery data:', error);
    }
  };

  const loadAllDeceased = async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
      const response = await fetch(`${baseUrl}/publicdeceased/search`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load deceased persons');
      }
      
      const data = await response.json();
      setAllDeceased(data);
      
      // Prepare search data source (full names with years)
      const names = data.map((d: DeceasedPerson) => formatDisplayName(d));
      setSearchResults(names);
    } catch (error) {
      console.error('Error loading deceased persons:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    
    // Clear selection when search is cleared
    if (!value) {
      setSelectedDeceased(null);
      setSearchResults(allDeceased.map(d => formatDisplayName(d)));
      setShowSearchResults(false);
      
      // Reset map view to cemetery center
      if (mapRef.current) {
        mapRef.current.flyTo(CEMETERY_BOUNDS.center, CEMETERY_BOUNDS.zoom, {
          duration: 1.5
        });
      }
      return;
    }
    
    // Always filter - no minimum length requirement for better UX
    // Filter deceased by name (case-insensitive, partial match)
    const searchLower = value.toLowerCase();
    const filtered = allDeceased.filter(d => {
      const displayName = formatDisplayName(d);
      
      // Build reverse name format: "LastName MiddleName FirstName"
      const reverseName = [
        d.lastName,
        d.middleName,
        d.firstName
      ].filter(Boolean).join(' ').toLowerCase();
      
      // Build reverse name with maiden name: "LastName (MaidenName) MiddleName FirstName"
      const reverseNameWithMaiden = d.maidenName 
        ? `${d.lastName} (${d.maidenName}) ${d.middleName || ''} ${d.firstName}`.toLowerCase().replace(/\s+/g, ' ').trim()
        : '';
      
      return displayName.toLowerCase().includes(searchLower) ||
        d.fullName.toLowerCase().includes(searchLower) ||
        d.firstName.toLowerCase().includes(searchLower) ||
        d.lastName.toLowerCase().includes(searchLower) ||
        reverseName.includes(searchLower) ||
        (reverseNameWithMaiden && reverseNameWithMaiden.includes(searchLower)) ||
        (d.maidenName && d.maidenName.toLowerCase().includes(searchLower));
    });
    
    const formattedResults = filtered.map(d => formatDisplayName(d));
    
    setSearchResults(formattedResults);
    setShowSearchResults(formattedResults.length > 0);
  };

  const handleSelectDeceased = (value: string) => {
    // Match by formatted display name
    const deceased = allDeceased.find(d => formatDisplayName(d) === value);
    
    if (deceased) {
      setSelectedDeceased(deceased);
      setSearchValue(value); // Keep the formatted name with years
      setShowSearchResults(false); // Hide dropdown after selection
      
      // Only show popup if location is not set
      if (!deceased.latitude || !deceased.longitude) {
        setShowDetailsPopup(true);
      } else {
        setShowDetailsPopup(false);
      }
    }
  };

  const handleMarkerClick = (deceased: DeceasedPerson) => {
    setSelectedDeceased(deceased);
    setSearchValue(formatDisplayName(deceased)); // Use formatted name with years
    setShowDetailsPopup(true);
  };

  const closeDetailsPopup = () => {
    setShowDetailsPopup(false);
  };

  return (
    <div className="public-search-container">
      {/* Search Bar - Floating Overlay */}
      <div className="search-bar-container">
        <div className="search-bar-content">
          <div className="search-title">
            <h1>Pokopališče Zagorje ob Savi</h1>
          </div>
          
          <div className="search-input-wrapper" style={{ position: 'relative' }} ref={searchWrapperRef}>
            <TextBox
              value={searchValue}
              onValueChanged={(e) => handleSearch(e.value)}
              valueChangeEvent="input"
              placeholder="🔍 Iskanje pokojnika..."
              showClearButton={true}
              stylingMode="filled"
              width="100%"
              height={48}
            />
            
            {/* Custom search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div 
                style={{
                  position: 'absolute',
                  top: '52px',
                  left: 0,
                  right: 0,
                  maxHeight: '300px',
                  overflow: 'auto',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '8px',
                  zIndex: 1001
                }}>
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectDeceased(result)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: index < searchResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {result}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map Container - Full Screen */}
      <div className="map-container" style={{ position: 'relative' }}>
        {/* Info Button - Bottom Right (above zoom controls) */}
        {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
          <button
            onClick={() => handleMarkerClick(selectedDeceased)}
            className="info-button"
            style={{
              position: 'absolute',
              right: '80px',
              zIndex: 1000,
              width: '48px',
              height: '64px',
              backgroundColor: '#fff',
              border: '2px solid rgba(0,0,0,0.2)',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Prikaži podrobnosti"
          >
            ℹ️
          </button>
        )}
        
        <MapContainer
          center={CEMETERY_BOUNDS.center}
          zoom={CEMETERY_BOUNDS.zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={false}
          ref={mapRef}
          maxZoom={22}
          whenReady={() => {
            // Prevent map from recalculating on window resize caused by popups
            if (mapRef.current) {
              setTimeout(() => {
                mapRef.current?.invalidateSize();
              }, 100);
            }
          }}
        >
          {/* Zoom Control - Bottom Right */}
          <ZoomControl position="bottomright" />
          
          {/* Base Layer: Esri World Imagery (free satellite imagery) */}
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={20}
            minZoom={3}
          />
          
          {/* ALTERNATIVE: OpenStreetMap (street map, not satellite) */}
          {/* <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            minZoom={3}
          /> */}
          
          {/* BACKUP: Sentinel-2 Cloudless (lower resolution, kept for fallback) */}
          {/* <TileLayer
            attribution='<a href="https://s2maps.eu" target="_blank">Sentinel-2 cloudless</a> by <a href="https://eox.at" target="_blank">EOX IT Services</a>'
            url="https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg"
            maxZoom={22}
            maxNativeZoom={18}
            minZoom={3}
          /> */}
          
          {/* Drone Photo Overlay - High-resolution cemetery imagery */}
          {cemeteryData && cemeteryData.photoPath && (
            <ImageOverlay
              url={`${import.meta.env.VITE_API_BASE_URL?.startsWith('http') ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') : window.location.origin}${cemeteryData.photoPath}`}
              bounds={[CEMETERY_BOUNDS.southwest, CEMETERY_BOUNDS.northeast]}
              opacity={1}
              zIndex={1000}
            />
          )}

          {/* Fly to selected location */}
          {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
            <FlyToLocation position={[selectedDeceased.latitude, selectedDeceased.longitude]} />
          )}

          {/* Marker for selected deceased */}
          {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
            <Marker 
              position={[selectedDeceased.latitude, selectedDeceased.longitude]}
              eventHandlers={{
                click: () => handleMarkerClick(selectedDeceased)
              }}
            />
          )}


        </MapContainer>
      </div>

      {/* Fullscreen Details Popup */}
      <Popup
        visible={showDetailsPopup}
        onHiding={closeDetailsPopup}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showTitle={true}
        showCloseButton={true}
        title={selectedDeceased?.fullName || ''}
        width="90%"
        height="90%"
        maxWidth={1200}
        position={{ my: 'center', at: 'center', of: window }}
        shading={true}
        shadingColor="rgba(0, 0, 0, 0.5)"
        wrapperAttr={{ style: { zIndex: 2000 } }}
        onShown={() => {
          // Prevent map dimension changes when popup opens
          document.body.style.overflow = 'hidden';
        }}
        onHidden={() => {
          // Restore scroll and invalidate map size when popup closes
          document.body.style.overflow = '';
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize();
            }
          }, 100);
        }}
      >
        <div style={{ padding: '0px', height: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {selectedDeceased && (
            <>
              {/* Grave Photo - Large Display */}
              {selectedDeceased.photoPath && (
                <div style={{ flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', backgroundColor: '#000', borderRadius: '8px', minHeight: '0' }}>
                  <img 
                    src={`${import.meta.env.VITE_API_BASE_URL?.startsWith('http') ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') : window.location.origin}${selectedDeceased.photoPath}`}
                    alt={`Grob ${selectedDeceased.fullName}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
              
              {/* Details Section */}
              <div style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '24px', color: '#333' }}>{selectedDeceased.fullName}</h2>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', fontSize: '16px' }}>
                  {selectedDeceased.displayBirthDate && (
                    <div>
                      <strong style={{ color: '#666' }}>Rojstvo:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.displayBirthDate}</div>
                    </div>
                  )}
                  
                  {selectedDeceased.displayDeathDate && (
                    <div>
                      <strong style={{ color: '#666' }}>Smrt:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.displayDeathDate}</div>
                    </div>
                  )}
                  
                  {/* {selectedDeceased.age && (
                    <div>
                      <strong style={{ color: '#666' }}>Starost:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.age} let</div>
                    </div>
                  )} */}
                  
                  {selectedDeceased.graveCode && (
                    <div>
                      <strong style={{ color: '#666' }}>Grob:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.graveCode}</div>
                    </div>
                  )}
                  
                  {selectedDeceased.graveType && (
                    <div>
                      <strong style={{ color: '#666' }}>Tip groba:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.graveType}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Popup>
    </div>
  );
};

export default PublicSearch;
